import React, { useState, useEffect } from 'react';
import { getAllBookings, createBooking, updateBooking, deleteBooking, getAllDoctors } from '../../api';
import styles from './CreateBooking.module.css';

const initialBookingState = {
  doctor: '',
  date: '',
  slot: '',
  fees: '',
};

const CreateBooking = () => {
  const [bookings, setBookings] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [form, setForm] = useState(initialBookingState);

  useEffect(() => {
    fetchBookings();
    fetchDoctors();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await getAllBookings();
      setBookings(data);
    } catch (error) {
      showAlert('error', 'Error fetching bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const data = await getAllDoctors();
      setDoctors(data);
    } catch (error) {
      showAlert('error', 'Error fetching doctors');
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
  };

  const resetForm = () => {
    setForm(initialBookingState);
    setSelectedBooking(null);
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (booking) => {
    setForm({
      doctor: booking.doctor?._id || '',
      date: booking.date ? booking.date.slice(0, 10) : '',
      slot: booking.slot,
      fees: booking.fees,
    });
    setSelectedBooking(booking);
    setShowEditModal(true);
  };

  const handleView = (booking) => {
    setSelectedBooking(booking);
    setShowViewModal(true);
  };

  const handleDelete = (booking) => {
    setSelectedBooking(booking);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === 'doctor') {
      const doc = doctors.find((d) => d._id === value);
      setForm((prev) => ({ ...prev, fees: doc ? doc.fees : '' }));
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const doctorName = booking.doctor?.name?.toLowerCase() || '';
    return doctorName.includes(searchTerm.toLowerCase());
  });

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!form.doctor || !form.date || !form.slot || !form.fees) {
      showAlert('error', 'All fields are required');
      return;
    }
    try {
      await createBooking(form);
      setShowAddModal(false);
      showAlert('success', 'Booking added successfully!');
      resetForm();
      fetchBookings();
    } catch (error) {
      showAlert('error', 'Error adding booking');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!form.doctor || !form.date || !form.slot || !form.fees) {
      showAlert('error', 'All fields are required');
      return;
    }
    try {
      await updateBooking(selectedBooking._id, form);
      setShowEditModal(false);
      showAlert('success', 'Booking updated successfully!');
      resetForm();
      fetchBookings();
    } catch (error) {
      showAlert('error', 'Error updating booking');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBooking(selectedBooking._id);
      setShowDeleteModal(false);
      showAlert('success', 'Booking deleted successfully!');
      resetForm();
      fetchBookings();
    } catch (error) {
      showAlert('error', 'Error deleting booking');
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading bookings...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Booking Management</h2>
        <p>Create, edit, delete, and view booking information</p>
      </div>

      {alert.show && (
        <div className={`${styles.alert} ${styles[`alert${alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}`]}`}>
          {alert.message}
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handleAdd}
        >
          <span>+</span> Add New Booking
        </button>
      </div>

      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search bookings by doctor name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        {filteredBookings.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No bookings found</h3>
            <p>{searchTerm ? 'Try adjusting your search terms' : 'Start by adding a new booking'}</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Doctor</th>
                <th>Date</th>
                <th>Slot</th>
                <th>Fees</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {filteredBookings.map((booking) => (
                <tr key={booking._id}>
                  <td>{booking.doctor?.name}</td>
                  <td>{booking.date ? booking.date.slice(0, 10) : ''}</td>
                  <td>{booking.slot}</td>
                  <td>{booking.fees}</td>
                  <td>{booking.status}</td>
                  <td className={styles.tableActions}>
                    <button className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`} onClick={() => handleView(booking)}>View</button>
                    <button className={`${styles.btn} ${styles.btnSmall} ${styles.btnWarning}`} onClick={() => handleEdit(booking)}>Edit</button>
                    <button className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`} onClick={() => handleDelete(booking)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Add Booking</h3>
              <button className={styles.closeBtn} onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form className={styles.form} onSubmit={handleAddSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Choose Doctor</label>
                <select
                  className={styles.select}
                  name="doctor"
                  value={form.doctor}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select Doctor</option>
                  {doctors.map((doc) => (
                    <option key={doc._id} value={doc._id}>{doc.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Choose Date</label>
                <input
                  className={styles.input}
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Slot</label>
                <input
                  className={styles.input}
                  type="text"
                  name="slot"
                  value={form.slot}
                  onChange={handleFormChange}
                  placeholder="e.g. 10:00 AM - 10:30 AM"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Fees</label>
                <input
                  className={styles.input}
                  type="number"
                  name="fees"
                  value={form.fees}
                  onChange={handleFormChange}
                  required
                  readOnly
                />
              </div>
              <div className={styles.formActions}>
                <button className={`${styles.btn} ${styles.btnPrimary}`} type="submit">Add</button>
                <button className={`${styles.btn} ${styles.btnSecondary}`} type="button" onClick={() => setShowAddModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Edit Booking</h3>
              <button className={styles.closeBtn} onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form className={styles.form} onSubmit={handleEditSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Choose Doctor</label>
                <select
                  className={styles.select}
                  name="doctor"
                  value={form.doctor}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select Doctor</option>
                  {doctors.map((doc) => (
                    <option key={doc._id} value={doc._id}>{doc.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Choose Date</label>
                <input
                  className={styles.input}
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Slot</label>
                <input
                  className={styles.input}
                  type="text"
                  name="slot"
                  value={form.slot}
                  onChange={handleFormChange}
                  placeholder="e.g. 10:00 AM - 10:30 AM"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Fees</label>
                <input
                  className={styles.input}
                  type="number"
                  name="fees"
                  value={form.fees}
                  onChange={handleFormChange}
                  required
                  readOnly
                />
              </div>
              <div className={styles.formActions}>
                <button className={`${styles.btn} ${styles.btnPrimary}`} type="submit">Update</button>
                <button className={`${styles.btn} ${styles.btnSecondary}`} type="button" onClick={() => setShowEditModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedBooking && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Booking Details</h3>
              <button className={styles.closeBtn} onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className={styles.detailItem}><span className={styles.detailLabel}>Doctor:</span> <span className={styles.detailValue}>{selectedBooking.doctor?.name}</span></div>
            <div className={styles.detailItem}><span className={styles.detailLabel}>Date:</span> <span className={styles.detailValue}>{selectedBooking.date ? selectedBooking.date.slice(0, 10) : ''}</span></div>
            <div className={styles.detailItem}><span className={styles.detailLabel}>Slot:</span> <span className={styles.detailValue}>{selectedBooking.slot}</span></div>
            <div className={styles.detailItem}><span className={styles.detailLabel}>Fees:</span> <span className={styles.detailValue}>{selectedBooking.fees}</span></div>
            <div className={styles.detailItem}><span className={styles.detailLabel}>Status:</span> <span className={styles.detailValue}>{selectedBooking.status}</span></div>
            <div className={styles.formActions}>
              <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Delete Booking</h3>
              <button className={styles.closeBtn} onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <p>Are you sure you want to delete this booking?</p>
            <div className={styles.formActions}>
              <button className={`${styles.btn} ${styles.btnDanger}`} onClick={handleDeleteConfirm}>Delete</button>
              <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBooking; 